import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService, apiHelpers } from '../services/api';
import ArticleCard from '../components/ArticleCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import './Reader.css';

const PER_PAGE = 20;
const INITIAL_TOPIC_LIMIT = 48;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [query, setQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [availableTags, setAvailableTags] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAllTopics, setShowAllTopics] = useState(false);

  useEffect(() => {
    loadTags();
    loadSources();

    const urlQuery = searchParams.get('q') || '';
    const urlTags = searchParams.get('tags') || '';
    const urlSource = searchParams.get('source') || '';
    const urlStartDate = searchParams.get('start_date') || '';
    const urlEndDate = searchParams.get('end_date') || '';

    setQuery(urlQuery);
    setSelectedTags(urlTags ? urlTags.split(',') : []);
    setSelectedSource(urlSource);
    setStartDate(urlStartDate);
    setEndDate(urlEndDate);

    if (urlQuery || urlTags || urlSource || urlStartDate || urlEndDate) {
      performSearch({
        query: urlQuery,
        tags: urlTags ? urlTags.split(',') : [],
        source: urlSource,
        start_date: urlStartDate,
        end_date: urlEndDate,
      });
    }
  }, []);

  const loadTags = async () => {
    try {
      const response = await apiService.getTags();
      setAvailableTags(response.data || []);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const loadSources = async () => {
    try {
      const response = await apiService.getSources();
      setAvailableSources(response.data || []);
    } catch (err) {
      console.error('Error loading sources:', err);
    }
  };

  const performSearch = async (searchRequest = null, page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const request = searchRequest || {
        query: query || null,
        tags: selectedTags.length > 0 ? selectedTags : null,
        source: selectedSource || null,
        start_date: startDate || null,
        end_date: endDate || null,
      };

      const response = await apiService.searchArticles(request, page, PER_PAGE);
      const data = response.data;

      setArticles(data.articles || []);
      setTotal(data.total || 0);
      setTotalPages(Math.ceil((data.total || 0) / PER_PAGE));
      setHasNext(data.has_next || false);
      setHasPrev(data.has_prev || false);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error searching articles:', err);
      setError(apiHelpers.formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedTags.length > 0) params.set('tags', selectedTags.join(','));
    if (selectedSource) params.set('source', selectedSource);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);

    setSearchParams(params);
    performSearch();
  };

  const handlePageChange = (page) => {
    performSearch(null, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTopicSearch = (tag) => {
    const nextTags = selectedTags.includes(tag) ? [] : [tag];
    setSelectedTags(nextTags);
    setShowAdvanced(true);

    const params = new URLSearchParams(searchParams);
    if (nextTags.length > 0) {
      params.set('tags', nextTags.join(','));
    } else {
      params.delete('tags');
    }
    if (query) params.set('q', query);
    if (selectedSource) params.set('source', selectedSource);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);

    setSearchParams(params);
    performSearch({
      query: query || null,
      tags: nextTags.length > 0 ? nextTags : null,
      source: selectedSource || null,
      start_date: startDate || null,
      end_date: endDate || null,
    });
  };

  const clearSearch = () => {
    setQuery('');
    setSelectedTags([]);
    setSelectedSource('');
    setStartDate('');
    setEndDate('');
    setArticles([]);
    setTotal(0);
    setSearchParams({});
  };

  const hasActiveSearch =
    query || selectedTags.length > 0 || selectedSource || startDate || endDate;

  const visibleTopics = showAllTopics
    ? availableTags
    : availableTags.slice(0, INITIAL_TOPIC_LIMIT);

  const placeholderText = useMemo(() => {
    const sourceCount = availableSources.length;
    const display = sourceCount > 0 ? sourceCount.toLocaleString() : '12,847';
    return `search ${display} sources →`;
  }, [availableSources.length]);

  return (
    <div className="pb-page">
      <header className="pb-page-header">
        <div>
          <span className="pb-page-eyebrow">▸ Reader · Search</span>
          <h1 className="pb-page-title">Search Articles</h1>
        </div>
        {hasActiveSearch && (
          <div className="pb-page-actions">
            <button
              type="button"
              className="pb-chip"
              onClick={clearSearch}
            >
              ↺ Clear
            </button>
          </div>
        )}
      </header>

      <form onSubmit={handleSearch} className="pb-search-form">
        <label className="pb-mono-tag" htmlFor="pb-search-query">
          ▸ Search Query
        </label>
        <div className="pb-search-input-wrap">
          <input
            id="pb-search-query"
            type="text"
            className="pb-search-input"
            placeholder={placeholderText}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="pb-search-row">
          <button
            type="button"
            className={`pb-chip ${showAdvanced ? 'is-active' : ''}`}
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-pressed={showAdvanced}
            aria-expanded={showAdvanced}
          >
            ⌥ Advanced Filters
          </button>
          <button
            type="submit"
            className="pb-chip is-primary"
            disabled={loading}
          >
            {loading ? '◌ Searching...' : '⌕ Search'}
          </button>
        </div>

        {showAdvanced && (
          <div className="pb-filter-panel">
            <div className="pb-filter-group">
              <label className="pb-mono-tag" htmlFor="pb-search-source">
                Source
              </label>
              <select
                id="pb-search-source"
                className="pb-field-input"
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
              >
                <option value="">All Sources</option>
                {availableSources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>

            <div className="pb-filter-group">
              <label className="pb-mono-tag" htmlFor="pb-search-start-date">
                Start Date
              </label>
              <input
                id="pb-search-start-date"
                type="date"
                className="pb-field-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="pb-filter-group">
              <label className="pb-mono-tag" htmlFor="pb-search-end-date">
                End Date
              </label>
              <input
                id="pb-search-end-date"
                type="date"
                className="pb-field-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        )}
      </form>

      {availableTags.length > 0 && (
        <section className="pb-topic-browser" aria-label="Topics">
          <div className="pb-section-head">
            <h2 className="pb-section-title">Topics</h2>
            <span className="pb-section-meta">
              {availableTags.length.toLocaleString()} available
            </span>
          </div>
          <div className="pb-chip-group">
            {visibleTopics.map((tag) => {
              const active = selectedTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  className={`pb-tag-chip ${active ? 'is-active' : ''}`}
                  onClick={() => handleTopicSearch(tag)}
                  aria-pressed={active}
                >
                  {tag}
                </button>
              );
            })}
          </div>
          {availableTags.length > INITIAL_TOPIC_LIMIT && (
            <button
              type="button"
              className="pb-chip"
              onClick={() => setShowAllTopics((value) => !value)}
            >
              {showAllTopics ? 'Show fewer topics' : `Show all ${availableTags.length.toLocaleString()} topics`}
            </button>
          )}
        </section>
      )}

      {loading && <LoadingSpinner text="Searching articles..." />}

      {error && (
        <div className="pb-error" role="alert">
          <span>
            <strong>Search Error:</strong> {error}
          </span>
          <button
            type="button"
            className="pb-chip"
            onClick={() => performSearch()}
          >
            Try Again
          </button>
        </div>
      )}

      {!loading && hasActiveSearch && articles.length === 0 && !error && (
        <div className="pb-empty">
          No matches yet — try a sharper topic.
        </div>
      )}

      {!loading && !hasActiveSearch && articles.length === 0 && (
        <div className="pb-empty">
          Type a query above. We&apos;ll scan headlines, summaries, and tags.
        </div>
      )}

      {articles.length > 0 && (
        <>
          <div className="pb-section-head">
            <h2 className="pb-section-title">Results</h2>
            <span className="pb-section-meta">
              {total.toLocaleString()} {total === 1 ? 'story' : 'stories'}
              {hasActiveSearch ? ' matched' : ''}
            </span>
          </div>
          <span className="pb-result-count" aria-live="polite">
            Found {total} articles
            {hasActiveSearch ? ' matching your search criteria' : ''}
          </span>

          <div className="pb-list" aria-label="Search results">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            hasNext={hasNext}
            hasPrev={hasPrev}
            total={total}
            perPage={PER_PAGE}
          />
        </>
      )}
    </div>
  );
};

export default SearchPage;
